'use client';
import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
}

interface FormErrors {
  name?: string;
  email?: string;
}

export default function NinetyDayBlueprintPage() {
  const [formData, setFormData] = useState<FormData>({ name: '', email: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate form submission (frontend only)
    setTimeout(() => {
      setIsSubmitting(false);
      setFormSubmitted(true);
    }, 800);
  };

  const blueprintModules = [
    {
      id: 'module_1',
      icon: 'CalendarDaysIcon',
      title: 'Sprint Planning Template',
      description: 'The exact Notion template we use to plan 2-week sprints with capacity buckets and deliverable tracking.'
    },
    {
      id: 'module_2',
      icon: 'ChartBarIcon',
      title: 'Channel Prioritization Framework',
      description: 'Decision matrix to identify which marketing channels deserve your focus based on your goals and resources.'
    },
    {
      id: 'module_3',
      icon: 'MapIcon',
      title: '90-Day Roadmap Builder',
      description: 'Step-by-step guide to building a realistic 90-day execution plan that accounts for actual capacity constraints.'
    },
    {
      id: 'module_4',
      icon: 'CalculatorIcon',
      title: 'Capacity Bucket Calculator',
      description: 'Spreadsheet tool to allocate hours across Always-On maintenance, Growth experiments, and Project work.'
    },
    {
      id: 'module_5',
      icon: 'BookOpenIcon',
      title: 'Module Library Guide',
      description: 'Complete reference of pre-built marketing modules with time estimates and implementation guides.'
    }
  ];

  const trustBadges = [
    { id: 'badge_1', icon: 'CheckBadgeIcon', label: 'US-Based', sublabel: 'Team & Operations' },
    { id: 'badge_2', icon: 'MapPinIcon', label: 'Las Vegas, NV', sublabel: 'Since 2000' },
    { id: 'badge_3', icon: 'CalendarIcon', label: 'Established 2000', sublabel: '25+ Years' },
    { id: 'badge_4', icon: 'BriefcaseIcon', label: '500+ Projects', sublabel: 'Delivered' }
  ];

  const testimonials = [
    {
      id: 'testimonial_1',
      quote: 'This framework completely changed how we approach marketing planning. We went from constant firefighting to predictable execution.',
      author: 'Sarah Chen',
      role: 'VP Marketing',
      company: 'TechFlow Solutions'
    },
    {
      id: 'testimonial_2',
      quote: 'The capacity bucket system is genius. Finally, a planning method that accounts for reality instead of wishful thinking.',
      author: 'Michael Torres',
      role: 'CMO',
      company: 'Horizon Hospitality Group'
    },
    {
      id: 'testimonial_3',
      quote: 'We implemented this blueprint and shipped more in 90 days than we had in the previous year. The sprint structure keeps us focused.',
      author: 'Jennifer Park',
      role: 'Director of Growth',
      company: 'Elevate Real Estate'
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background via-background to-surface-1">
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 px-6 overflow-hidden">
          {/* Dark cinematic background with grain texture */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background" />
          <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }} />
          
          <div className="relative max-w-[1400px] mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <span className="inline-block text-micro uppercase tracking-ultra-wide text-primary mb-6 font-semibold">
                Free Execution Framework
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1] text-foreground">
                The 90-Day Execution Sprint{' '}
                <span className="text-primary" style={{ textShadow: '0 0 40px rgba(220, 38, 38, 0.3)' }}>
                  Blueprint
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-4 leading-relaxed">
                The exact framework we use to plan and execute marketing sprints for our clients. No theory. No fluff. Just the system that ships real work every week.
              </p>
              <p className="text-sm text-muted-foreground/80 italic">
                Used by marketing teams to go from constant firefighting to predictable execution.
              </p>
            </div>

            {/* Form Card */}
            <div className="max-w-xl mx-auto">
              {!formSubmitted ? (
                <div className="p-8 md:p-10 border border-border bg-card shadow-2xl">
                  <h2 className="text-h3 mb-2 text-center text-foreground">Get the Blueprint</h2>
                  <p className="text-body-sm text-muted-foreground text-center mb-8">
                    Enter your details to receive instant access
                  </p>
                  
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                    <div>
                      <Input
                        label="Name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                        }}
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-2 text-sm text-destructive flex items-center gap-2">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {formErrors.name}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Input
                        label="Email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
                        }}
                        required
                      />
                      {formErrors.email && (
                        <p className="mt-2 text-sm text-destructive flex items-center gap-2">
                          <Icon name="ExclamationCircleIcon" size={16} />
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                    
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Get the Blueprint'}
                    </Button>
                    
                    <p className="text-micro uppercase tracking-widest text-muted-foreground text-center">
                      No spam, unsubscribe anytime
                    </p>
                  </form>
                </div>
              ) : (
                // Download Delivery Success State
                <div className="p-12 md:p-16 border border-primary bg-card shadow-2xl text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={48} className="text-primary" />
                  </div>
                  
                  <h2 className="text-h2 mb-4 text-foreground">Check Your Email</h2>
                  
                  <p className="text-body text-muted-foreground mb-2">
                    We just sent the 90-Day Execution Sprint Blueprint to:
                  </p>
                  <p className="text-lg font-semibold text-foreground mb-8">
                    {formData.email}
                  </p>
                  
                  <div className="bg-surface-1 border border-border p-6 mb-8 text-left">
                    <h3 className="text-h5 mb-4 text-foreground">Next Steps:</h3>
                    <ol className="space-y-3 text-body-sm text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">1.</span>
                        <span>Check your inbox (and spam folder) for an email from Best Odds Corp</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">2.</span>
                        <span>Download the blueprint PDF and supporting templates</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">3.</span>
                        <span>Start planning your next 90-day sprint using the framework</span>
                      </li>
                    </ol>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link href="/book">
                      <Button variant="primary" size="lg" className="w-full sm:w-auto">
                        Book Strategy Call
                      </Button>
                    </Link>
                    <Link href="/">
                      <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                        Back to Home
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Blueprint Preview Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block text-micro uppercase tracking-ultra-wide text-primary mb-4 font-semibold">
                What's Inside
              </span>
              <h2 className="text-h2 mb-6 text-foreground">5 Modules to Transform Your Execution</h2>
              <p className="text-body text-muted-foreground max-w-2xl mx-auto">
                Everything you need to plan, prioritize, and execute marketing work that actually ships.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blueprintModules.map((module) => (
                <div
                  key={module.id}
                  className="p-8 border border-border bg-card hover:border-primary/50 transition-all duration-300 group"
                >
                  <div className="w-14 h-14 mb-6 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon name={module.icon as any} size={28} className="text-primary" />
                  </div>
                  <h3 className="text-h4 mb-3 text-foreground">{module.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">
                    {module.description}
                  </p>
                </div>
              ))}
              
              {/* Bonus Card */}
              <div className="p-8 border-2 border-primary bg-primary/5 relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <span className="inline-block px-3 py-1 bg-primary text-primary-foreground text-micro uppercase tracking-widest font-bold">
                    Bonus
                  </span>
                </div>
                <div className="w-14 h-14 mb-6 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Icon name="SparklesIcon" size={28} className="text-primary" />
                </div>
                <h3 className="text-h4 mb-3 text-foreground">Real Campaign Examples</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  Case studies showing how we've used this framework with real clients across different industries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges Section */}
        <section className="py-20 px-6 bg-surface-1 border-y border-border">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {trustBadges.map((badge) => (
                <div
                  key={badge.id}
                  className="p-6 border border-border bg-card text-center hover:border-primary/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name={badge.icon as any} size={24} className="text-primary" />
                  </div>
                  <div className="text-h5 mb-1 text-foreground">{badge.label}</div>
                  <div className="text-micro uppercase tracking-widest text-muted-foreground">
                    {badge.sublabel}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="py-24 px-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block text-micro uppercase tracking-ultra-wide text-primary mb-4 font-semibold">
                Trusted by Operators
              </span>
              <h2 className="text-h2 mb-6 text-foreground">What Marketing Leaders Say</h2>
              <p className="text-body text-muted-foreground max-w-2xl mx-auto">
                Real feedback from teams using this framework to execute with confidence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="p-8 border border-border bg-card hover:border-primary/50 transition-all duration-300"
                >
                  <div className="mb-6">
                    <Icon name="ChatBubbleLeftIcon" size={32} className="text-primary/30" />
                  </div>
                  <p className="text-body mb-6 leading-relaxed italic text-muted-foreground">
                    "{testimonial.quote}"
                  </p>
                  <div className="pt-6 border-t border-border">
                    <div className="font-semibold text-foreground">{testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                    <div className="text-micro uppercase tracking-widest text-muted-foreground mt-1">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-32 px-6 border-t border-border bg-gradient-to-b from-background to-surface-1">
          <div className="max-w-[1400px] mx-auto text-center">
            <h2 className="text-h1 mb-6 text-foreground">Ready to Execute?</h2>
            <p className="text-body text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
              Download the blueprint and start planning your next 90 days. Or book a call and we'll build it with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/book">
                <Button variant="primary" size="lg" className="w-full sm:w-auto">
                  Book Strategy Call
                </Button>
              </Link>
              <Link href="/pricing">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}